/* Copyright (c) 2020-present CloudIO Inc */

import {
  Avatar,
  Badge,
  Box,
  Paper,
  Popper,
  Theme,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import type { VirtualElement } from '@popperjs/core';
import clsx from 'clsx';
import tinyColor from 'tinycolor2';
import { getAvatarChars } from 'common/functions';
import { BaseProps, DomProps, UserPic } from 'componentregistry';
import { useUserProfile } from 'components/UserPic/UserProfileCache';
import zIndex from 'constants/ZIndex';
import { useStringExpression } from 'expression/useExpression';
import useDebounce from 'hooks/useDebounce';
import { getColor } from 'init/initFunctions';
import React, { useLayoutEffect, useRef, useState } from 'react';
import useTrigger from 'triggers/useTrigger';
import useJoinRef from 'utils/useJoinRef';
import { useStateXAtomValue } from '@cloudio-saas/statex';
import { activesAtom } from 'utils/Active';
import UserProfilePopover from './UserProfilePopover';
import { stopPropagation } from 'utils/events';

const useStyles = makeStyles((theme: Theme) => ({
  popper: {
    padding: '0',
    visibility: 'visible',
    zIndex: zIndex.Tooltip,
    '&[data-popper-placement^="bottom"] > $arrow': {
      top: 0,
      left: 0,
      marginTop: '-8px',
      width: '24px',
      height: '9px',
      '&::before': {
        borderWidth: '0 8px 8px 8px',
        borderColor: `transparent transparent ${theme.palette.background.paper} transparent`,
      },
    },
    '&[data-popper-placement^="top"] > $arrow': {
      bottom: 0,
      left: 0,
      marginBottom: '-8px',
      width: '24px',
      height: '9px',
      '&::before': {
        borderWidth: '8px 8px 0 8px',
        borderColor: `${theme.palette.background.paper} transparent transparent transparent`,
      },
    },
    '&[data-popper-placement^="right"] > $arrow': {
      left: 0,
      top: 0,
      marginLeft: '-8px',
      height: '24px',
      width: '9px',
      '&::before': {
        borderWidth: '8px 8px 8px 0',
        borderColor: `transparent ${theme.palette.background.paper} transparent transparent`,
      },
    },
    '&[data-popper-placement^="left"] > $arrow': {
      right: 0,
      top: 0,
      marginRight: '-8px',
      height: '24px',
      width: '9px',
      '&::before': {
        borderWidth: '8px 0 8px 8px',
        borderColor: `transparent transparent transparent ${theme.palette.background.paper}`,
      },
    },
  },
  arrow: {
    position: 'absolute',
    fontSize: 7,
    zIndex: 1,
    width: '24px',
    height: '24px',
    '&::before': {
      content: '""',
      margin: 'auto',
      display: 'block',
      width: 0,
      height: 0,
      borderStyle: 'solid',
    },
  },
  popperHidden: {
    visibility: 'hidden',
  },
}));

interface State {
  el?: VirtualElement | null;
  userName?: string;
}

export function UserPicPopper({
  userName,
  children,
  anchorEl,
  onHide,
  offset,
}: {
  onHide: () => void;
  userName?: string;
  anchorEl?: VirtualElement | null;
  children?: React.ReactNode;
  offset?: [number, number];
}) {
  const classes = useStyles();
  const [state, setState] = useState<State>({ el: anchorEl, userName });
  const mouseOnPopper = useRef(false);
  const [hide, cancelHide] = useDebounce(() => {
    setState({ el: null, userName: '' });
    onHide();
  });
  const [show, cancelShow] = useDebounce((s: State) => {
    setState(s);
  });
  useLayoutEffect(() => {
    if (!mouseOnPopper.current) {
      cancelHide();
      cancelShow();
      show(500, { el: anchorEl, userName });
    }
  }, [anchorEl, cancelHide, cancelShow, show, userName]);

  if (!(state.el && state.userName)) {
    return null;
  }
  return (
    <Popper
      className={clsx(classes.popper)}
      open
      anchorEl={state.el}
      placement="top"
      onMouseEnter={(e) => {
        cancelHide();
        mouseOnPopper.current = true;
        show(0, state);
      }}
      onMouseLeave={() => {
        cancelShow();
        mouseOnPopper.current = false;
        hide(500);
      }}
      onMouseDown={stopPropagation}
      onClick={stopPropagation}
      // CanvasGrid is capturing mouse move and highlighting the row under this popper
      onMouseMove={stopPropagation}
      modifiers={[
        {
          name: 'flip',
          enabled: true,
          options: {
            altBoundary: true,
            rootBoundary: 'document',
            padding: 8,
          },
        },
        {
          name: 'preventOverflow',
          enabled: true,
          options: {
            altAxis: true,
            mainAxis: true,
            altBoundary: true,
            tether: true,
            boundary: 'document',
            rootBoundary: 'document',
            padding: 8,
          },
        },
        {
          name: 'arrow',
          enabled: true,
          options: {
            padding: 8,
          },
        },
        {
          name: 'offset',
          enabled: true,
          options: { offset: offset ?? [0, 10] },
        },
      ]}
    >
      <div data-popper-arrow className={classes.arrow} />
      <Paper
        elevation={3}
        sx={{ bgcolor: 'background.paper', backgroundImage: 'none' }}
      >
        <UserProfilePopover
          userName={state.userName}
          onClose={() => {
            hide(500);
          }}
        >
          {children}
        </UserProfilePopover>
      </Paper>
    </Popper>
  );
}

interface Props extends Required<UserPic.UserPic>, BaseProps<'UserPic'> {
  value?: string | null;
}

function UserPicComp(
  {
    userName,
    onClick,
    domProps,
    className,
    size = 40,
    children,
    showUserName,
    showEmailAddress,
    hideImage,
    hideOnlineStatus,
  }: {
    userName: string | null | undefined;
    onClick?: () => void;
    domProps?: DomProps;
    className?: string;
    size?: number;
    children?: React.ReactNode;
    showUserName?: boolean;
    showEmailAddress?: boolean;
    hideImage?: boolean;
    hideOnlineStatus?: boolean;
  },
  ref: React.Ref<HTMLElement>,
) {
  const record = useUserProfile(userName);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const r = useRef<HTMLElement>(null);
  const status = useStateXAtomValue(activesAtom, {
    params: { userName: userName ?? '_' },
  });
  const joinRef = useJoinRef(ref, r);
  const bgcolor = getColor(record?.emailAddress ?? userName);
  return (
    <>
      <Box
        {...domProps}
        className={className}
        onClick={onClick}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          verticalAlign: 'middle',
        }}
        ref={joinRef}
        onMouseEnter={(e) => {
          setAnchorEl(r.current);
        }}
        onMouseLeave={() => {
          setAnchorEl(null); // allow some time for the user to move the cursor over to the popover's content
        }}
      >
        {!hideImage && (
          <Badge
            variant={status && !hideOnlineStatus ? 'dot' : undefined}
            overlap="circular"
            color={status === 'active' ? 'success' : 'warning'}
          >
            <Avatar
              alt={userName ?? 'Missing User Name'}
              src={record?.profileImage ?? ''}
              sx={{
                bgcolor: record?.profileImage ? undefined : bgcolor,
                width: size,
                height: size,
                fontSize: `${size * 0.5}px`,
                color: tinyColor
                  .mostReadable(bgcolor, ['#fff', '#000'])
                  .toHexString(),
              }}
              data-tip={record ? undefined : `Unknown user: ${userName}`}
            >
              {getAvatarChars(
                record?.displayName ?? userName ?? record?.emailAddress,
              )}
            </Avatar>
          </Badge>
        )}
        {showUserName && (
          <Typography sx={{ color: 'text.primary' }}>
            {record?.displayName || userName}
          </Typography>
        )}
        {showEmailAddress && (
          <Typography sx={{ color: 'text.secondary' }}>
            {showUserName ? ` (${record?.emailAddress})` : record?.emailAddress}
          </Typography>
        )}
      </Box>
      {record ? (
        <UserPicPopper
          anchorEl={anchorEl}
          userName={record.userName}
          onHide={() => {
            setAnchorEl(null);
          }}
        >
          {children}
        </UserPicPopper>
      ) : null}
    </>
  );
}

export const UserProfilePic = React.forwardRef(UserPicComp);

function IOUserPic(props: Props, ref: React.Ref<HTMLElement>) {
  const {
    appUid,
    roleUid,
    platform,
    itemId,
    pageId,
    domProps,
    userName,
    className,
    size,
    children,
    showUserName,
    showEmailAddress,
    hideImage,
    hideOnlineStatus,
    value,
  } = props;
  const userNameResult = useStringExpression({
    exp: value
      ? { type: 'text', value }
      : { type: 'expression', value: userName },
    context: { appUid, pageId, itemId, property: 'userName', data: {} },
  });
  const [onClick, clickInProgress] = useTrigger(
    {
      appUid,
      roleUid,
      platform,
      pageId,
      itemId,
    },
    'TriggerOnClick',
  );
  return (
    <UserProfilePic
      className={className}
      domProps={domProps}
      onClick={clickInProgress ? undefined : onClick}
      ref={ref}
      userName={userNameResult}
      size={size}
      showUserName={showUserName}
      showEmailAddress={showEmailAddress}
      hideImage={hideImage}
      hideOnlineStatus={hideOnlineStatus}
    >
      {children}
    </UserProfilePic>
  );
}

export default React.forwardRef(IOUserPic);
